import React from "react";
import dandarLogo from "../../../assets/images/dandar-logo.png";
import { ALL_VITALS_CONFIG, IDoctorPrescriptionData } from "../../../types/prescription";

interface PrescriptionPadViewProps {
  prescriptionData: Partial<IDoctorPrescriptionData>;
  defaultDoctorData?: {
    name?: string;
    education?: string | string[];
    specialization?: string[];
    registrationNumber?: string;
    hospitalName?: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    mobileNumber?: string;
  };
  mode?: "template" | "sample";
  containerId?: string;
}

export const PrescriptionPadView: React.FC<PrescriptionPadViewProps> = ({
  prescriptionData,
  defaultDoctorData,
  mode = "template",
  containerId = "prescription-pad-print",
}) => {
  // Doctor details resolution
  const doctorName =
    prescriptionData.doctorName ||
    defaultDoctorData?.name ||
    "Doctor Name";

  const specialisations =
    prescriptionData.specialisations &&
    (Array.isArray(prescriptionData.specialisations)
      ? prescriptionData.specialisations.length > 0
      : Boolean(prescriptionData.specialisations))
      ? prescriptionData.specialisations
      : defaultDoctorData?.specialization || [];

  const rawEducation =
    prescriptionData.education ||
    (Array.isArray(defaultDoctorData?.education)
      ? defaultDoctorData?.education.join(", ")
      : defaultDoctorData?.education) ||
    "";

  const registrationNumber =
    prescriptionData.registrationNumber ||
    defaultDoctorData?.registrationNumber ||
    "";

  const vitalsChecklist = prescriptionData.vitalsChecklist || [];

  // Address 1: Current City / Dandar Health Clinic
  const currentCity = defaultDoctorData?.city || "Current City";
  const currentHospital = defaultDoctorData?.hospitalName || "";
  const currentAddressStr = [
    currentHospital,
    defaultDoctorData?.address,
    defaultDoctorData?.city ? `${defaultDoctorData.city}${defaultDoctorData?.pincode ? ` - ${defaultDoctorData.pincode}` : ""}` : "",
    defaultDoctorData?.state,
  ]
    .filter(Boolean)
    .join(", ");

  // Address 2: Doctor's Personal Clinic (clinic city, clinic address, mobile)
  const personalClinicAddress = prescriptionData.selfClinicAddress || "";
  const personalClinicMobile = prescriptionData.selfClinicMobile || "";

  // Parse personal clinic city and address (joined as "clinic city - address")
  let personalCity = "";
  let personalAddress = "";
  if (personalClinicAddress) {
    const dashIdx = personalClinicAddress.indexOf(" - ");
    if (dashIdx !== -1) {
      personalCity = personalClinicAddress.substring(0, dashIdx).trim();
      personalAddress = personalClinicAddress.substring(dashIdx + 3).trim();
    } else {
      personalAddress = personalClinicAddress.trim();
    }
  }

  // Selected vitals metadata
  const selectedVitals = ALL_VITALS_CONFIG.filter((v) =>
    vitalsChecklist.includes(v.key),
  );

  return (
    <div
      id={containerId}
      className="w-full bg-white text-gray-900 shadow-sm border border-gray-200 rounded-lg p-6 sm:p-8 flex flex-col justify-between"
      style={{
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        minHeight: "1050px",
        boxSizing: "border-box",
      }}
    >
      {/* ── TOP SECTION (HEADER, PATIENT, VITALS & BODY) ── */}
      <div className="w-full flex-1 flex flex-col">
        {/* ── HEADER ── */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pb-4 border-b border-gray-200">
          {/* Logo on Left */}
          <div className="flex-shrink-0">
            <img
              src={dandarLogo}
              alt="Dandar Health Logo"
              className="h-12 sm:h-14 w-auto object-contain"
            />
          </div>

          {/* Doctor Info on Right */}
          <div className="text-left sm:text-right max-w-lg">
            {/* Doctor Name - Green Color, Compact Size */}
            <h1 className="text-base sm:text-lg font-bold text-[#199C4D] tracking-tight leading-snug">
              {doctorName.startsWith("Dr") ? doctorName : `Dr. ${doctorName}`}
            </h1>

            {/* Specialisations in Red/Maroon */}
            {((Array.isArray(specialisations) && specialisations.length > 0) ||
              (typeof specialisations === "string" && specialisations)) && (
              <p className="text-[10px] sm:text-[11px] font-bold text-[#A33474] uppercase tracking-wide mt-0.5">
                {Array.isArray(specialisations) ? specialisations.join(", ") : specialisations}
              </p>
            )}

            {/* Qualifications - Natural Wrapping */}
            {rawEducation && (
              <div className="text-[10px] sm:text-[11px] font-medium text-gray-700 mt-0.5 max-w-sm sm:ml-auto break-words leading-tight whitespace-pre-line">
                {rawEducation}
              </div>
            )}

            {/* Registration Number */}
            {registrationNumber && (
              <p className="text-[10px] sm:text-[11px] font-medium text-gray-600 mt-0.5">
                Reg No. {registrationNumber}
              </p>
            )}
          </div>
        </div>

        {/* ── PATIENT INFORMATION SECTION ── */}
        <div className="my-4">
          <div className="text-[#771D51] font-bold text-xs tracking-wider uppercase mb-1.5 px-0.5">
            PATIENT
          </div>
          <div className="bg-[#FBFBFC] border border-[#E9E9EE] rounded-lg p-3 px-4 text-xs sm:text-[13px]">
            {mode === "template" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8 text-gray-800">
                <div className="space-y-2">
                  <div>
                    <span className="font-bold text-[#771D51]">NAME: </span>
                    <span className="text-gray-400"></span>
                  </div>
                  <div>
                    <span className="font-bold text-[#771D51]">DATE: </span>
                    <span className="text-gray-400"></span>
                  </div>
                  <div>
                    <span className="font-bold text-[#771D51]">DHP ID: </span>
                    <span className="text-gray-400"></span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <span className="font-bold text-[#771D51]">PRESCRIPTION ID: </span>
                    <span className="text-gray-400"></span>
                  </div>
                  <div>
                    <span className="font-bold text-[#771D51]">MOBILE: </span>
                    <span className="text-gray-400"></span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8 text-gray-800">
                <div className="space-y-2">
                  <div>
                    <span className="font-bold text-[#771D51]">NAME: </span>
                    <span className="font-semibold text-gray-900">
                      New User (11 months 30 days / male)
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-[#771D51]">DATE: </span>
                    <span className="font-medium text-gray-800">
                      {new Date().toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-[#771D51]">DHP ID: </span>
                    <span className="font-semibold text-gray-900">DHP_00755</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <span className="font-bold text-[#771D51]">PRESCRIPTION ID: </span>
                    <span className="font-semibold text-gray-900">DHR_00755</span>
                  </div>
                  <div>
                    <span className="font-bold text-[#771D51]">MOBILE: </span>
                    <span className="font-medium text-gray-800">9918490897</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── VITALS SECTION (4 per row with enhanced margin) ── */}
        <div className="my-5">
          <div className="text-[#771D51] font-bold text-xs tracking-wider uppercase mb-1.5 px-0.5">
            VITALS
          </div>
          <div className="bg-[#FBFBFC] border border-[#E9E9EE] rounded-lg p-3.5 px-4 text-xs sm:text-[13px]">
            {selectedVitals.length > 0 ? (
              <div className="grid grid-cols-4 gap-x-6 gap-y-2.5">
                {selectedVitals.map((vital) => (
                  <div key={vital.key} className="flex items-baseline gap-1.5 min-w-0">
                    <span className="text-gray-600 font-medium shrink-0">{vital.shortLabel}:</span>
                    <span className="font-bold text-gray-900 truncate">
                      {mode === "template" ? (
                        <span className="text-gray-400 font-normal"></span>
                      ) : (
                        vital.sampleValue
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400 italic text-xs">
                No vitals checklist configured for this doctor.
              </div>
            )}
          </div>
        </div>

        {/* ── PRESCRIPTION BODY SPACE (Clean & Blank) ── */}
        <div className="w-full flex-1 min-h-[420px] bg-white" />
      </div>

      {/* ── FOOTER (Continuation lines, center aligned, not pointwise) ── */}
      <div className="mt-auto pt-4 w-full">
        <div className="border-b border-gray-300 pb-2.5 mb-2 text-[11px] sm:text-xs text-gray-800 leading-relaxed text-center">
          <p className="m-0">
            <span className="font-bold text-gray-900">{currentCity} -: </span>
            <span>{(currentAddressStr || "Dandar Health Partner Clinic").replace(/\.+$/, "")}.</span>
          </p>

          {personalClinicAddress && (
            <p className="m-0 mt-1">
              {personalCity ? (
                <span className="font-bold text-gray-900">{personalCity} -: </span>
              ) : (
                <span className="font-bold text-gray-900">Personal Clinic -: </span>
              )}
              <span>{(personalAddress || personalClinicAddress).replace(/\.+$/, "")}</span>
              {personalClinicMobile && (
                <span className="ml-1 font-medium text-gray-800">
                  , Mob: {personalClinicMobile}
                </span>
              )}
              .
            </p>
          )}
        </div>

        {/* Medicolegal Disclaimer in Maroon */}
        <div className="text-center text-[10px] sm:text-[11px] font-medium text-[#A33474]">
          Disclaimer : This prescription is valid in India only and not valid for any Medicolegal purpose.
        </div>
      </div>
    </div>
  );
};

export default PrescriptionPadView;
